import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2, Zap } from "lucide-react";
import RobotMascot from "../components/RobotMascot";
import { IMAGES } from "../content";
import { BusinessSection, OwnerSection, SecondOwnerSection, DocumentsSection, SignSection } from "../components/apply/steps";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emailOk = (v) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v);

const validate = (f, files) => {
  const e = {};
  if (!f.business_legal_name.trim()) e.business_legal_name = "Required";
  if (!f.legal_entity) e.legal_entity = "Required";
  if (f.ein.replace(/\D/g, "").length !== 9) e.ein = "Enter a valid 9-digit EIN";
  if (!f.business_phone.trim()) e.business_phone = "Required";
  if (!f.business_address.trim()) e.business_address = "Required";
  if (!f.start_date) e.start_date = "Required";
  if (!f.industry) e.industry = "Required";
  if (!f.business_description.trim()) e.business_description = "Tell us a little about the business";
  if (!f.avg_monthly_revenue || Number(f.avg_monthly_revenue) <= 0) e.avg_monthly_revenue = "Enter your average monthly revenue";
  if (!f.requested_amount || Number(f.requested_amount) <= 0) e.requested_amount = "Enter the funding amount you need";
  if (!f.use_of_funds.length) e.use_of_funds = "Pick at least one use of funds";
  if (!f.owner_full_name.trim()) e.owner_full_name = "Required";
  if (!emailOk(f.owner_email)) e.owner_email = "Enter a valid email";
  if (!f.owner_mobile.trim()) e.owner_mobile = "Required";
  if (f.owner_ssn.replace(/\D/g, "").length !== 9) e.owner_ssn = "Enter a valid 9-digit SSN";
  if (!f.owner_dob) e.owner_dob = "Required";
  if (!f.owner_home_address.trim()) e.owner_home_address = "Required";
  if (f.add_second_owner) {
    if (!f.second_owner.name.trim()) e.so_name = "Required";
    if (!emailOk(f.second_owner.email)) e.so_email = "Enter a valid email";
    if (!f.second_owner.mobile.trim()) e.so_mobile = "Required";
  }
  if (files.length < 1) e.files = "Attach at least one bank statement — required for underwriting";
  if (!f.agree) e.agree = "Please agree to the authorization to continue";
  if (!f.signature.trim()) e.signature = "Type your full name to sign";
  return e;
};

const SECTIONS = [
  { id: "sec-business", n: "01", title: "Business Information", sub: "The company applying for funding.", quip: "Just the basics — I only need 60 seconds of your time.", Component: BusinessSection },
  { id: "sec-owner", n: "02", title: "Owner Information", sub: "Primary owner (20%+ stake).", quip: "SSNs are encrypted in transit. I never peek.", Component: OwnerSection },
  { id: "sec-second", n: "03", title: "Second owner", sub: "Optional — add if there's another owner with a 20%+ stake.", quip: "More than one captain? Impressive.", Component: SecondOwnerSection },
  { id: "sec-docs", n: "04", title: "Bank statements & documents", sub: "Attach your last 4 months of business bank statements (PDF or image). REQUIRED for underwriting.", quip: "Feed me statements. I'm hungry for data.", Component: DocumentsSection },
  { id: "sec-sign", n: "05", title: "Verification & Signature", sub: "Read, agree, sign.", quip: "Last step. Sign, and I'll start reading.", Component: SignSection },
];

const Success = ({ reference, navigate, bridge, prefill }) => (
  <div className="relative min-h-screen flex items-center justify-center px-5 py-24 overflow-hidden" data-testid="apply-success">
    <div className="absolute inset-0 bg-grid bg-grid-fade" aria-hidden="true" />
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="relative w-full max-w-lg rounded-[2rem] border border-black/5 bg-white p-10 text-center shadow-[0_20px_60px_rgba(0,0,0,0.08)]"
    >
      <img src={IMAGES.thumbs} alt="Robot giving a thumbs up" className="w-40 mx-auto" draggable="false" />
      <h1 className="font-display text-3xl font-bold text-[#0A1628] mt-4">Application received.</h1>
      <p data-testid="apply-reference" className="mono mt-5 inline-block rounded-full bg-[#007AFF]/10 text-[#007AFF] px-5 py-2 text-sm font-semibold">
        Reference {reference}
      </p>
      <p className="mt-5 text-sm leading-relaxed text-[#52525B]">
        Robo-B7 is reading your file now. Most decisions arrive inside 24 hours — a team member
        will call you before any hard credit pull.
      </p>
      {bridge && (
        <div className="mt-7 rounded-2xl border border-[#14D8A5]/40 bg-[#14D8A5]/10 p-5 text-left">
          <p className="label-mono text-[#0E7C5E] mb-2">Next steps for your micro advance</p>
          <ol className="space-y-1.5 text-sm text-[#0A1628]">
            <li>1. Upload your last 4 months of statements</li>
            <li>2. Robo-B7 auto-approves up to $5,000 instantly</li>
            <li>3. Sign your contract — funds same business day</li>
          </ol>
        </div>
      )}
      {bridge ? (
        <button
          data-testid="apply-success-bridge"
          onClick={() => navigate("/bridge", { state: { prefill } })}
          className="group mt-7 w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-[#14D8A5] px-7 py-4 text-base font-semibold text-[#0A1628] shadow-[0_12px_32px_rgba(20,216,165,0.35)] transition-all duration-300 hover:bg-[#0A1628] hover:text-white hover:-translate-y-0.5"
        >
          Next: upload statements &amp; get approved
          <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
        </button>
      ) : null}
      <button
        data-testid="apply-success-back"
        onClick={() => navigate("/")}
        className={`w-full rounded-full py-4 text-base font-semibold transition-all duration-300 ${
          bridge
            ? "mt-3 border border-black/10 text-[#0A1628] hover:border-black/25"
            : "mt-9 bg-[#0A1628] text-white hover:bg-[#007AFF]"
        }`}
      >
        Back to lendrobot.com
      </button>
    </motion.div>
  </div>
);

const ApplyPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const bridgeMode = !!location.state?.bridge;
  const [form, setForm] = useState(() => ({
    requested_amount: location.state?.amount ? String(location.state.amount) : "",
    avg_monthly_revenue: "",
    use_of_funds: [],
    business_legal_name: "",
    legal_entity: "",
    ein: "",
    business_phone: "",
    website: "",
    industry: "",
    business_address: "",
    start_date: "",
    rent_or_own: "Rent",
    open_balances: "No",
    business_description: "",
    owner_full_name: "",
    owner_email: "",
    owner_mobile: "",
    owner_ownership_pct: "",
    owner_ssn: "",
    owner_dob: "",
    owner_home_address: "",
    owner_credit_score: "",
    add_second_owner: false,
    second_owner: { name: "", email: "", mobile: "", pct: "", ssn: "", dob: "", home_address: "" },
    agree: false,
    signature: "",
  }));
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [zoneErr, setZoneErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");
  const [submitError, setSubmitError] = useState("");

  const ctx = useMemo(() => ({
    form,
    errors,
    files,
    setFiles,
    zoneErr,
    setZoneErr,
    set: (k, v) => setForm((f) => ({ ...f, [k]: v })),
    setSO: (k, v) => setForm((f) => ({ ...f, second_owner: { ...f.second_owner, [k]: v } })),
    toggleUse: (v) => setForm((f) => ({
      ...f,
      use_of_funds: f.use_of_funds.includes(v) ? f.use_of_funds.filter((x) => x !== v) : [...f.use_of_funds, v],
    })),
  }), [form, errors, files, zoneErr]);

  const scrollTop = () => (window.__lenis ? window.__lenis.scrollTo(0, { immediate: true }) : window.scrollTo(0, 0));

  const submit = async () => {
    const e = validate(form, files);
    setErrors(e);
    if (Object.keys(e).length) {
      scrollTop();
      return;
    }
    setLoading(true);
    setSubmitError("");
    try {
      const fd = new FormData();
      fd.append("payload", JSON.stringify(form));
      files.forEach((f) => fd.append("files", f));
      const res = await fetch(`${API}/applications`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Submit failed");
      setResult(data.reference);
      scrollTop();
    } catch (err) {
      setSubmitError("The robot dropped the connection — please try submitting again.");
      scrollTop();
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <Success
        reference={result}
        navigate={navigate}
        bridge={bridgeMode}
        prefill={{
          business_name: form.business_legal_name,
          email: form.owner_email,
          phone: form.owner_mobile,
        }}
      />
    );
  }

  return (
    <div className="relative min-h-screen pb-24 overflow-hidden">
      <div className="absolute inset-0 bg-grid bg-grid-fade" aria-hidden="true" />
      <div className="absolute top-0 right-[-15%] w-[36rem] h-[36rem] rounded-full bg-[#00E5FF]/15 blur-[130px]" aria-hidden="true" />
      <div className="absolute bottom-0 left-[-15%] w-[32rem] h-[32rem] rounded-full bg-[#14D8A5]/15 blur-[130px]" aria-hidden="true" />

      <div className="relative mx-auto max-w-3xl px-5 md:px-8 pt-24 md:pt-28">
        <button
          data-testid="apply-exit"
          onClick={() => navigate("/")}
          className="group inline-flex items-center gap-2 text-sm font-medium text-[#52525B] hover:text-[#0A1628] transition-colors"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" /> lendrobot.com
        </button>

        <div className="mt-6 flex items-start justify-between gap-6">
          <div>
            <p className="label-mono text-[#007AFF] mb-3">FREE · NO IMPACT TO CREDIT AT THIS STAGE</p>
            <h1 className="font-display font-bold tracking-[-0.02em] leading-[1.02] text-[#0A1628] text-3xl md:text-5xl">
              Business Funding Application
            </h1>
            <p className="mt-4 max-w-md text-sm md:text-base leading-relaxed text-[#52525B]">
              Tell us about your business and the capital you need. Most decisions inside 24
              hours. Information stays confidential.
            </p>
          </div>
          <span className="lr-float-slow hidden sm:block shrink-0 mt-2">
            <RobotMascot className="w-16" />
          </span>
        </div>

        {bridgeMode && (
          <div data-testid="apply-bridge-banner" className="mt-6 rounded-2xl border border-[#14D8A5]/40 bg-[#14D8A5]/10 px-5 py-4">
            <p className="text-sm leading-relaxed text-[#0A1628]">
              <Zap className="inline w-4 h-4 text-[#0E7C5E] mr-1.5 -mt-0.5" />
              <strong>Micro advance up to $5,000.</strong> After this application you'll upload
              your statements, get auto-approved on the spot, and sign your contract instantly.
            </p>
          </div>
        )}

        {submitError && (
          <p data-testid="apply-submit-error" className="mt-6 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-xs font-medium text-red-600">
            {submitError}
          </p>
        )}

        <form
          onSubmit={(e) => { e.preventDefault(); submit(); }}
          data-testid="apply-form"
          className="relative mt-10 space-y-6"
        >
          {SECTIONS.map(({ id, n, title, sub, quip, Component }) => (
            <section
              key={id}
              id={id}
              data-testid={id}
              className="relative rounded-[2rem] border border-black/5 bg-white/85 backdrop-blur-xl p-6 md:p-10 shadow-[0_8px_32px_rgba(0,0,0,0.05)]"
            >
              <div className="flex items-start justify-between gap-4 mb-8">
                <div>
                  <p className="label-mono text-[#007AFF] mb-2">{n}</p>
                  <h2 className="font-display text-2xl md:text-3xl font-bold text-[#0A1628]">{title}</h2>
                  <p className="mt-2 text-sm text-[#52525B]">{sub}</p>
                </div>
                <span className="lr-float-slow hidden sm:block shrink-0">
                  <RobotMascot className="w-12" />
                </span>
              </div>

              <div className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-[#007AFF]/20 bg-[#007AFF]/5 px-4 py-2">
                <span className="h-2 w-2 rounded-full bg-[#14D8A5] animate-pulse" />
                <span className="mono text-[11px] text-[#007AFF]">ROBO-B7 · {quip}</span>
              </div>

              <Component ctx={ctx} />
            </section>
          ))}

          <div className="flex flex-col items-center gap-4 pt-4">
            <button
              type="submit"
              data-testid="apply-submit-button"
              disabled={loading}
              className="group inline-flex w-full sm:w-auto items-center justify-center gap-2.5 rounded-full bg-[#007AFF] px-10 py-4 text-base font-semibold text-white shadow-[0_12px_32px_rgba(0,122,255,0.35)] transition-all duration-300 hover:bg-[#0A1628] hover:-translate-y-0.5 disabled:opacity-60 disabled:pointer-events-none"
            >
              {loading ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Robo-B7 is reading your file…</>
              ) : (
                <>Submit application <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" /></>
              )}
            </button>
            <p className="mono text-[11px] text-[#52525B] text-center">
              Submitting does not impact your personal credit · A team member contacts you before any hard pull
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApplyPage;
